import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, Signal, signal } from '@angular/core';
import { catchError, forkJoin, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoginResponse } from '../interfaces/login-response';
import { Router } from '@angular/router';
import { USE_SESSION_TOKEN, SKIP_AUTH, SKIP_ERROR_HANDLER } from '../../core/auth-context';
import { SessionService } from '../../core/services/session.service';
import { ShopCartsService } from '../../trading/shop-carts/shop-carts.service';

const baseUrl = environment.trazeApi;
const apiUrl = environment.apiUrl;

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private http = inject(HttpClient);
  private router = inject(Router);
  private sessionService = inject(SessionService);
  private shopCartsService = inject(ShopCartsService);

  private sessionTokenKey = 'session_token';
  private authTokenKey = 'auth_token';

  invalidAuthToken = signal<boolean>(false);
  lastError = signal<string | null>(null);

  signIn(userName: string | null, password: string | null, appSubscription: string | null): Observable<any> {

    this.lastError.set(null);
    this.invalidAuthToken.set(false);

    let headers = new HttpHeaders()

    if (appSubscription) {
      headers = headers.set('appSubscriptionId', appSubscription);
    }

    const context = new HttpContext().set(SKIP_ERROR_HANDLER, true);

    return this.http.post<LoginResponse>(`${apiUrl}/security/login`,
      { userName, password, application: 'webshop' }, { headers, context })
      .pipe(
        tap(res => {
          this.setSessionToken(res.token);
          this.setAuthToken(res.authorizationToken);
        }),
        switchMap((res) => {
          return this.getSession()
        }),
        catchError((err) => {
          this.lastError.set(err.error?.message || 'Error al iniciar sesión');
          return throwError(() => err);
        })
      );
  }

  getSession(): Observable<any> {

    const context = new HttpContext().set(USE_SESSION_TOKEN, true);

    return this.http.post<any>(`${apiUrl}/security/getSession`, {}, { context }).pipe(
      tap(session => {

        this.sessionService.setSession(session);

        if (this.hasAuthToken()) {
          this.shopCartsService.refreshShopCarts();
        }
      }),
      map(() => {
        if (this.hasAuthToken()) {
          return true;
        } else {
          this.invalidAuthToken.set(true);
          return false;
        }
      })
    );
  }

  changeSessionSubscription(appSubscriptionId: string): Observable<any> {

    return this.http.post(`${baseUrl}/security/changeSessionSubscription`, {
      id: appSubscriptionId
    }).pipe(
      tap((res: any) => {
        localStorage.setItem('appSubscriptionId', res.id);
      })
    );
  }

  isLoggedIn(): Observable<boolean> {

    const sessionToken = this.getSessionToken();
    const authToken = this.getAuthToken();

    if (!sessionToken || !authToken) {
      return of(false);
    }

    if (this.sessionService.session()) {
      return of(true);
    }

    return this.getSession().pipe(
      catchError((error: any) => {
        this.logout();
        return of(false)
      })
    );
  }

  logout(): void {

    this.invalidAuthToken.set(false);
    this.lastError.set(null);

    this.setAuthToken(null);
    this.setSessionToken(null);

    this.sessionService.clearSession();
    /* this.router.navigateByUrl('/auth'); */
    window.location.href = '/login';
  }

  getSessionToken(): string | null {
    return localStorage.getItem(this.sessionTokenKey);
  }

  getAuthToken(): string | null {
    return localStorage.getItem(this.authTokenKey);
  }

  private setSessionToken(token: string | null): void {

    if (!token) {
      localStorage.removeItem(this.sessionTokenKey);
    } else {
      localStorage.setItem(this.sessionTokenKey, token);
    }
  }

  private setAuthToken(token: string | null): void {

    if (!token) {
      localStorage.removeItem(this.authTokenKey);
    } else {
      localStorage.setItem(this.authTokenKey, token);
    }
  }

  private hasAuthToken(): boolean {
    return !!localStorage.getItem(this.authTokenKey);
  }
}
